import S from 's-js';
import * as React from 'react';
import * as $ from 'jquery';
import { SuperCriticalComponent, SuperCriticalReference } from 'lib/supercritical';
import 'lib/jquery-ui-sortable-loader';

const STASHED_PROPS_DATA_KEY = '__pc_sortable_stashed_props',
    JQUERY_UI_SORTABLE_KEY = 'ui-sortable';

interface PCSortableProps<TItem> {
    // Sort fn when an item is dragged w/in this list or to this list from another list
    onSort : (item : TItem, targetItems : TItem[], sourceItems : TItem[] | null) => void;
    items : () => TItem[];
    rowFactory : (item : TItem, idx : number) => React.ReactElement<any> | null,
    className? : string;
    // TODO: Would this be more performant as a primitive boolean instead of as a function?
    // The trade offs are this:
    // A function means toggling the enabled/disabled state of the PCSortable only needs
    // to enable/disable the jQuery UI sortable w/o needing to re-init the whole thing.
    // That's nice. But a function also means it's very tempting for calling code to pass an
    // arrow function for this prop, which will be a different JS function object each time
    // this component gets called, causing SuperCriticalComponent to update the props signal
    // and SuperCriticalReference to re-run our init function. The effect is that any change
    // that causes the calling component to re-render also causes us to destroy and re-init
    // the jQuery UI sortable. The concern is that that might be slow, especially if there's
    // some reason the calling component might re-render often. Calling code can avoid this
    // issue by storing the disabled function as a viewmodel property instead of constructing
    // a new arrow function in the view on each render. But that's easy to forget.
    // A primitive boolean reverses this: Toggling the enabled/disabled state would mean we need
    // to re-init the whole jQuery UI sortable, which could be slow. But a primitive boolean also
    // avoids the whole issue of unneeded re-inits of the jQuery UI sortable when the calling
    // component re-renders.
    disabled? : () => boolean;
    // TODO: Ditto disabled prop re perf considerations of a function vs a primitive string.
    connectWith? : () => string;
}

export class PCSortable<TItem> extends SuperCriticalComponent<PCSortableProps<TItem>> {
    ref : (e : HTMLUListElement | null) => void;

    constructor(props : PCSortableProps<TItem>) {
        super(props);
        this.ref = SuperCriticalReference(this, InitializeSortable);
    }

    srender({ items, rowFactory, className } : PCSortableProps<TItem>) {
        return (
            <ul className={className} ref={this.ref}>{items().map(rowFactory)}</ul>
        );
    }
}

function InitializeSortable<TItem>(props : PCSortableProps<TItem>, el : HTMLUListElement) {
    const $el = $(el);

    // TH 12/14/2016 -- Use jquery data API to support connected lists.
    $el.data(STASHED_PROPS_DATA_KEY, props);
    S.cleanup(() => $el.removeData(STASHED_PROPS_DATA_KEY));

    const { items, disabled, connectWith } = props;

    let deferredCallbacks = [] as (() => void)[],
        startIndex = 0, // index of dragged item in source list
        draggedItem = null as null | TItem;

    S.cleanup(() => {
        deferredCallbacks = [];
        draggedItem = null;
    });

    // TH 12/14/2016 -- NOTE: References to KO in comments are from original knockout-sortable-binding implementation.
    $el.sortable({
        // For connected lists, fires on the source list
        start: function (event, ui) {
            // start event. jQuery UI fires this when drag gesture starts.
            // By default, when you drag a sortable item, jQuery UI rearranges the DOM to match the outcome of your sort.
            // It does this before firing the beforeStop, stop, update, or receive events.
            // This leads to corrupt state if KO later tries to update the DOM itself (duplicated items and other weirdness).
            // The fix is to set the _noFinalSort flag. That causes jQuery UI not to rearrange the DOM.
            // As a side effect, it also means jQuery UI will never fire the update event. That's OK b/c we'll listen for beforeStop instead.
            // NOTE: _noFinalSort flag supported as of jQuery UI v1.11
            const sortable = $el.data(JQUERY_UI_SORTABLE_KEY);
            sortable._noFinalSort = true;
            startIndex = $el.children().index(ui.item);
            draggedItem = items()[startIndex];
        },
        // For connected lists, fires on the source list
        beforeStop: function (event, ui) {
            // beforeStop event. jQuery UI fires this after user has completed their drag gesture, but while jQuery UI's helper and placeholder elements are still in the DOM.
            // We'll use the position of the placeholder element to determine the user's intended sort position.
            // (B/c we set the _noFinalSort flag above, the dragged element itself will still be in its original position).
            // Based on that new position, we'll fire the target list's sort callback w/ the appropriate data.
            // jQuery UI will remove the helper and placeholder elements for us after this event fires.

            const targetList = ui.placeholder.parent(),
                targetListProps = targetList.data(STASHED_PROPS_DATA_KEY) as PCSortableProps<TItem> | undefined;

            if (!targetListProps || !draggedItem) return;

            const { items: targetItems, onSort } = targetListProps,
                item = ui.item[0],
                placeholder = ui.placeholder[0],
                targetChildren = targetList.children(),
                targetChildrenData = targetItems().filter(item => item !== draggedItem),
                sourceChildrenData = targetList[0] === $el[0] ? null : items().filter(item => item !== draggedItem);

            let newIndex = 0;

            for (let i = 0; i < targetChildren.length; i++) {
                if (targetChildren[i] === placeholder) {
                    break;
                }
                if (targetChildren[i] !== item) newIndex++;
            }

            targetChildrenData.splice(newIndex, 0, draggedItem);

            // Queue callback on the target list. Fire with following arguments:
            //   1) itemData           -- {Object} The KO viewModel object for the dragged item.
            //   2) targetChildrenData -- {Array} A plain JS array containing the KO viewModels for the target list's children.
            //                            Lists children data in up-to-date sort order. Includes dragged item in its correct sort order.
            //   3) sourceChildrenData -- {Array or Null} Null if the sort started and ended on the same list.
            //                            Otherwise, if sort dragged item from one connected list to another, will be a plain JS array containing
            //                            the KO viewModels for the source list's children, in up-to-date sort order. I.e., will be same as the children
            //                            list from before the sort, except w/ the dragged item removed.
            deferredCallbacks.push(() => draggedItem && onSort(draggedItem, targetChildrenData, sourceChildrenData));
        },
        // For connected lists, fires on the source list
        stop: function (event, ui) {
            // stop event. jQuery UI fires this event after the user has completed their drag and the placeholder and helper elements have been removed.
            // Fire the callbacks queued in the beforeStop event.
            // We do this here instead of in beforeStop to prevent KO-triggered DOM manipulations from interfering w/ jQuery UI's DOM clean up.
            deferredCallbacks.map(callback => callback());
            deferredCallbacks = [];
            startIndex = 0;
            draggedItem = null;
        },
        // Tells jQuery UI to make a placeholder element w/ the specified CSS class. Otherwise, there'd be no placeholder element at all.
        // We need this for our beforeStop handler to work.
        placeholder: 'sortable-placeholder'
    });
    S.cleanup(() => $el.sortable('destroy'));

    if (connectWith) S(() => $el.sortable('option', 'connectWith', connectWith!()));
    if (disabled)    S(() => disabled!() ? $el.sortable('disable') : $el.sortable('enable'));
}


// WEBPACK FOOTER //
// ./apps/portcall/pcsortable/view.tsx